import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api, { resolveImg } from "../lib/api";
import { NavLinks, SearchOverlay, MobileDrawer } from "./header/HeaderParts";
import { CurrencySelect, HeaderIcons } from "./header/HeaderActions";
import { logger } from "../lib/logger";

const Brand = ({ logo, to }) => (
  <Link to={to} data-testid="brand-logo" className="lg:w-1/3 flex justify-center items-center">
    {logo ? (
      <img src={resolveImg(logo)} alt="OMR PERFUMER" className="h-12 md:h-16 w-auto object-contain" />
    ) : (
      <span className="serif text-[#F2EBE1] text-lg md:text-2xl tracking-[0.18em] md:tracking-[0.3em] font-medium whitespace-nowrap">OMR PERFUMER</span>
    )}
  </Link>
);

export const Header = () => {
  const { t, toggle, lang, currency, setCurrency, currencies } = useLang();
  const { count, setOpen } = useCart();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobile, setMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setLogo(r.data.logo_url || null)).catch((e) => logger.error("settings load failed", e));
  }, []);

  const nav = [
    { to: "/", label: t("nav_home") },
    { to: "/shop", label: t("nav_shop") },
    { to: "/story", label: t("nav_story") },
    { to: "/perfumer", label: t("nav_perfumer") },
    { to: "/contact", label: t("nav_contact") },
    { to: "/experiments", label: t("nav_lab") },
  ];
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  const doSearch = (e) => {
    e.preventDefault();
    setSearchOpen(false);
    navigate(`/shop?q=${encodeURIComponent(q)}`);
    setQ("");
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#0B0A08]/90 backdrop-blur-xl border-b border-[#262421]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-center justify-between h-[84px]">
            <div className="flex items-center gap-4 lg:w-1/3">
              {user && (
                <>
                  <button className="lg:hidden text-[#F2EBE1]" onClick={() => setMobile(true)} data-testid="mobile-menu-open" aria-label="Menu">
                    <Menu size={22} strokeWidth={1.4} />
                  </button>
                  <NavLinks items={nav.slice(0, 3)} isActive={isActive} className="hidden lg:flex items-center gap-8" />
                </>
              )}
            </div>
            <Brand logo={logo} to={user ? "/" : "/welcome"} />
            <div className="flex items-center justify-end gap-5 lg:w-1/3">
              <NavLinks items={user ? nav.slice(3) : nav.slice(5)} isActive={isActive} className={`${user ? "hidden lg:flex" : "flex"} items-center gap-8 me-2`} />
              <button onClick={toggle} data-testid="language-toggle" className="text-xs uppercase tracking-[0.15em] text-[#EAE3D8] hover:text-white transition-colors border border-[#4A4A46] px-2 py-1">
                {lang === "en" ? "عربي" : "EN"}
              </button>
              {user && <CurrencySelect currency={currency} setCurrency={setCurrency} currencies={currencies} />}
              {user && <HeaderIcons count={count} onSearch={() => setSearchOpen(true)} onCart={() => setOpen(true)} />}
            </div>
          </div>
        </div>
      </header>
      {searchOpen && <SearchOverlay q={q} setQ={setQ} onSubmit={doSearch} onClose={() => setSearchOpen(false)} placeholder={t("search") + "…"} />}
      {mobile && <MobileDrawer items={nav} user={user} isAdmin={isAdmin} t={t} onClose={() => setMobile(false)} />}
    </>
  );
};
